import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Filter, X, ChevronRight, Shield, AlertTriangle, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { browseIndex, browseMatrix } from "../lib/api";
import { cn } from "../lib/utils";

const RISK_TONE = {
  HIGH: "bg-rose-50 text-rose-700 border-rose-200",
  MODERATE: "bg-amber-50 text-amber-800 border-amber-200",
  LOW: "bg-lime-50 text-lime-700 border-lime-200",
};

const RISK_ICON = {
  HIGH: AlertCircle,
  MODERATE: AlertTriangle,
  LOW: Shield,
};

function RiskPill({ level }) {
  const Icon = RISK_ICON[level] || Shield;
  return (
    <span
      data-testid={`browse-risk-pill-${level}`}
      className={cn(
        "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border uppercase tracking-wide",
        RISK_TONE[level] || RISK_TONE.LOW,
      )}
    >
      <Icon className="w-3 h-3" />
      {level}
    </span>
  );
}

export default function Browse() {
  const [index, setIndex] = useState(null);
  const [q, setQ] = useState("");
  const [medId, setMedId] = useState("");
  const [ingId, setIngId] = useState("");
  const [risk, setRisk] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const idx = await browseIndex();
        setIndex(idx);
      } catch (_e) {
        /* ignore */
      }
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await browseMatrix({
          q,
          medicationId: medId,
          ingredientId: ingId,
          riskLevel: risk,
          page,
          limit: 20,
        });
        if (!cancelled) setData(res);
      } catch (_e) {
        if (!cancelled) setData({ items: [], total: 0 });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [q, medId, ingId, risk, page]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((data.total || 0) / 20)),
    [data.total],
  );

  const hasFilters = q || medId || ingId || risk;

  return (
    <main data-testid="browse-page" className="max-w-screen-xl mx-auto px-4 sm:px-6 md:px-10 py-10 sm:py-14">
      <header className="mb-8 sm:mb-10">
        <p className="text-[11px] uppercase tracking-[0.16em] text-ink-mute mb-3">
          Does it react?
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-ink">
          Browse the full matrix.
        </h1>
        <p className="mt-4 text-base sm:text-lg text-ink-soft max-w-2xl">
          Every documented interaction between prescription medications and skincare
          ingredients we track — searchable, filterable, and deep-linkable.
        </p>
        {index?.totals && (
          <div
            data-testid="browse-totals"
            className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-[13px] text-ink-mute"
          >
            <span><b className="text-ink">{index.totals.medications}</b> medications</span>
            <span><b className="text-ink">{index.totals.ingredients}</b> ingredients</span>
            <span><b className="text-ink">{index.totals.interactions}</b> interactions</span>
          </div>
        )}
      </header>

      {/* Filter bar */}
      <section className="rounded-2xl border border-surface-line bg-white/70 p-4 sm:p-5 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-5 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-mute" />
            <input
              data-testid="browse-search-input"
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Search medications or ingredients…"
              className="w-full pl-9 pr-3 h-11 rounded-xl border border-surface-line bg-white text-sm focus:outline-none focus:border-ink"
            />
          </div>
          <select
            data-testid="browse-med-filter"
            value={medId}
            onChange={(e) => { setMedId(e.target.value); setPage(1); }}
            className="md:col-span-3 h-11 rounded-xl border border-surface-line bg-white px-3 text-sm"
          >
            <option value="">All medications</option>
            {index?.medications?.map((m) => (
              <option key={m.id} value={m.id}>{m.name || m.generic_name}</option>
            ))}
          </select>
          <select
            data-testid="browse-ing-filter"
            value={ingId}
            onChange={(e) => { setIngId(e.target.value); setPage(1); }}
            className="md:col-span-3 h-11 rounded-xl border border-surface-line bg-white px-3 text-sm"
          >
            <option value="">All ingredients</option>
            {index?.ingredients?.map((i) => (
              <option key={i.id} value={i.id}>{i.name}</option>
            ))}
          </select>
          <select
            data-testid="browse-risk-filter"
            value={risk}
            onChange={(e) => { setRisk(e.target.value); setPage(1); }}
            className="md:col-span-1 h-11 rounded-xl border border-surface-line bg-white px-3 text-sm"
          >
            <option value="">Any</option>
            <option value="HIGH">High</option>
            <option value="MODERATE">Moderate</option>
            <option value="LOW">Low</option>
          </select>
        </div>
        {hasFilters && (
          <button
            data-testid="browse-clear-filters"
            onClick={() => { setQ(""); setMedId(""); setIngId(""); setRisk(""); setPage(1); }}
            className="mt-3 inline-flex items-center gap-1.5 text-[12px] text-ink-mute hover:text-ink"
          >
            <X className="w-3.5 h-3.5" /> Clear filters
          </button>
        )}
      </section>

      {/* Results */}
      <section data-testid="browse-results">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[13px] text-ink-mute">
            {loading ? "Loading…" : `${data.total} result${data.total === 1 ? "" : "s"}`}
          </p>
          <div className="flex items-center gap-2">
            <Button
              data-testid="browse-prev-page"
              variant="outline" size="sm"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >Prev</Button>
            <span className="text-[12px] text-ink-mute">
              Page {page} / {totalPages}
            </span>
            <Button
              data-testid="browse-next-page"
              variant="outline" size="sm"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >Next</Button>
          </div>
        </div>

        {!loading && data.items.length === 0 && (
          <div
            data-testid="browse-empty"
            className="rounded-2xl border border-dashed border-surface-line bg-white/60 p-10 text-center text-ink-mute"
          >
            No interactions match these filters.
          </div>
        )}

        <ul className="space-y-3">
          {data.items.map((row) => {
            const med = row.medication || {};
            const ing = row.ingredient || {};
            return (
              <li
                key={row.id}
                data-testid={`browse-row-${row.id}`}
                className="group rounded-2xl border border-surface-line bg-white p-4 sm:p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-4 flex-wrap">
                  <div className="flex-1 min-w-[280px]">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <RiskPill level={row.risk_level} />
                      {row.confidence_level && (
                        <span className="text-[10.5px] uppercase tracking-wide text-ink-mute">
                          {row.confidence_level.replace(/_/g, " ")}
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-ink leading-snug">
                      <Link
                        to={`/browse/med/${med.id}`}
                        data-testid={`browse-row-med-${row.id}`}
                        className="hover:underline"
                      >
                        {med.name || med.generic_name || "Unknown med"}
                      </Link>
                      <span className="text-ink-mute mx-2">×</span>
                      <Link
                        to={`/browse/ing/${ing.id}`}
                        data-testid={`browse-row-ing-${row.id}`}
                        className="hover:underline"
                      >
                        {ing.name || "Unknown ingredient"}
                      </Link>
                    </h3>
                    {row.summary && (
                      <p className="mt-2 text-sm text-ink-soft max-w-2xl">{row.summary}</p>
                    )}
                    {row.recommendation && (
                      <p className="mt-2 text-sm text-ink">
                        <b>What to do:</b> {row.recommendation}
                      </p>
                    )}
                  </div>
                  <Link
                    to={`/browse/pair/${med.id}/${ing.id}`}
                    data-testid={`browse-row-pair-${row.id}`}
                    className="inline-flex items-center gap-1 text-sm text-ink hover:text-ink group-hover:translate-x-0.5 transition-transform"
                  >
                    Details <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      </section>
    </main>
  );
}
